/**
 * Admin Ledger Health Page
 *
 * Dedicated page for ledger processing health monitoring:
 * - Processing status overview (completed, pending, failed, processing lag)
 * - Gap detection with start/end/size details
 * - Failed ledger tracking with error messages and retry counts
 * - Recovery actions (trigger gap recovery, retry failures, recover range)
 * - Ingestion service recovery status (when available)
 * - Recent ledger list with processing status
 */

import React, { useEffect, useState, useCallback } from 'react';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';

// ==================== API ====================

const API_BASE = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:3703/api/v1';

async function fetchWithAuth(endpoint: string) {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const res = await fetch(`${API_BASE}${endpoint}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function postWithAuth(endpoint: string, body?: unknown) {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const res = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ==================== Types ====================

interface LedgerStats {
  statusCounts: Record<string, number>;
  latestIndex: number;
  firstIndex: number;
  totalTracked: number;
  failedWithRetries: number;
  lag: {
    latestHealthy: number;
    latestCompleted: number;
    pendingCount: number;
    processingCount: number;
  };
}

interface Gap {
  start: number;
  end: number;
  size: number;
}

interface GapData {
  gaps: Gap[];
  gapCount: number;
  totalMissing: number;
  range: {
    first: number;
    last: number;
    expected: number;
    actual: number;
  };
}

interface Failure {
  ledgerIndex: number;
  errorMessage: string | null;
  retryCount: number;
  createdAt: string;
  updatedAt: string;
  processingStartedAt: string | null;
  processingCompletedAt: string | null;
}

interface FailureData {
  totalFailed: number;
  failures: Failure[];
}

interface RecentLedger {
  ledger_index: number;
  ledger_hash: string;
  close_time: string;
  processing_status: string;
  nft_transaction_count: number;
  retry_count: number;
  error_message: string | null;
  created_at: string;
  processing_completed_at: string | null;
}

interface RecoveryStatus {
  status: string;
  ledgerRange?: {
    first: number;
    last: number;
    current: number;
    tracked: number;
    gap: number;
  };
  processingStats?: {
    completed: number;
    failed: number;
    inProgress: number;
    pending: number;
    totalTransactions: number;
    totalNftTransactions: number;
  };
  completeness?: {
    percentage: string;
    behindCurrent: number;
    estimatedTimeBehind: string;
  };
}

// ==================== Helpers ====================

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    completed: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    processing: 'bg-blue-100 text-blue-800',
    failed: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  );
}

function StatCard({ label, value, subtext, variant = 'default' }: {
  label: string;
  value: string | number;
  subtext?: string;
  variant?: 'default' | 'success' | 'warning' | 'error';
}) {
  const styles = {
    default: 'bg-white border-gray-200',
    success: 'bg-green-50 border-green-200',
    warning: 'bg-yellow-50 border-yellow-200',
    error: 'bg-red-50 border-red-200',
  };
  return (
    <div className={`rounded-lg border p-4 ${styles[variant]}`}>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
    </div>
  );
}

// ==================== Component ====================

export function AdminLedgerHealthPage() {
  const [stats, setStats] = useState<LedgerStats | null>(null);
  const [gapData, setGapData] = useState<GapData | null>(null);
  const [failureData, setFailureData] = useState<FailureData | null>(null);
  const [recentLedgers, setRecentLedgers] = useState<RecentLedger[]>([]);
  const [recoveryStatus, setRecoveryStatus] = useState<RecoveryStatus | null>(null);
  const [recoveryUnavailable, setRecoveryUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [statsRes, gapsRes, failuresRes, recentRes, recoveryRes] = await Promise.all([
        fetchWithAuth('/admin/ledger-health/stats'),
        fetchWithAuth('/admin/ledger-health/gaps'),
        fetchWithAuth('/admin/ledger-health/failures'),
        fetchWithAuth('/admin/ledger-health/recent'),
        fetchWithAuth('/admin/ledger-health/recovery-status').catch(() => null),
      ]);
      setStats(statsRes.data);
      setGapData(gapsRes.data);
      setFailureData(failuresRes.data);
      setRecentLedgers(recentRes.data.recentLedgers);
      if (recoveryRes?.data && !recoveryRes.serviceUnavailable) {
        setRecoveryStatus(recoveryRes.data);
        setRecoveryUnavailable(false);
      } else {
        setRecoveryStatus(null);
        setRecoveryUnavailable(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load ledger health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAction = async (action: string, body?: unknown) => {
    setActionLoading(action);
    setActionResult(null);
    try {
      const result = await postWithAuth(`/admin/ledger-health/${action}`, body);
      setActionResult({
        type: 'success',
        message: result.data?.message || result.data?.status || 'Action triggered successfully',
      });
      // Reload data after a short delay
      setTimeout(loadData, 2000);
    } catch (err: any) {
      setActionResult({ type: 'error', message: err.message || 'Action failed' });
    } finally {
      setActionLoading(null);
    }
  };

  const lagValue = stats
    ? Math.max(0, stats.latestIndex - (stats.lag.latestCompleted || stats.latestIndex))
    : 0;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ledger Health</h1>
            <p className="mt-1 text-sm text-gray-600">
              Ledger processing status, gap detection, and recovery
            </p>
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {error && <ErrorDisplay error={error} onRetry={loadData} />}

        {/* Action result banner */}
        {actionResult && (
          <div className={`rounded-lg border p-3 text-sm ${
            actionResult.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            {actionResult.message}
            <button onClick={() => setActionResult(null)} className="ml-2 underline">dismiss</button>
          </div>
        )}

        {loading && !stats && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent" />
              <p className="mt-4 text-sm text-gray-600">Loading ledger health data...</p>
            </div>
          </div>
        )}

        {stats && (
          <>
            {/* Status Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard
                label="Completed"
                value={(stats.statusCounts.completed || 0).toLocaleString()}
                variant="success"
              />
              <StatCard
                label="Pending"
                value={(stats.statusCounts.pending || 0).toLocaleString()}
                variant={(stats.statusCounts.pending || 0) > 10 ? 'warning' : 'default'}
              />
              <StatCard
                label="Failed"
                value={(stats.statusCounts.failed || 0).toLocaleString()}
                variant={(stats.statusCounts.failed || 0) > 0 ? 'error' : 'default'}
              />
              <StatCard
                label="Processing Lag"
                value={`${lagValue} ledgers`}
                variant={lagValue > 5 ? 'warning' : 'default'}
              />
              <StatCard
                label="Total Tracked"
                value={stats.totalTracked.toLocaleString()}
                subtext={`#${stats.firstIndex.toLocaleString()} - #${stats.latestIndex.toLocaleString()}`}
              />
            </div>

            {/* Ingestion Service Recovery Status */}
            {recoveryStatus?.completeness && (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-indigo-700">Recovery Service Status</h3>
                  <StatusBadge status={recoveryStatus.status === 'operational' ? 'completed' : recoveryStatus.status} />
                </div>
                <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <div className="text-xs text-indigo-500">Completeness</div>
                    <div className="text-lg font-bold text-indigo-700">{recoveryStatus.completeness.percentage}%</div>
                  </div>
                  <div>
                    <div className="text-xs text-indigo-500">Behind Current</div>
                    <div className="text-lg font-bold text-indigo-700">{recoveryStatus.completeness.behindCurrent.toLocaleString()} ledgers</div>
                  </div>
                  <div>
                    <div className="text-xs text-indigo-500">Est. Time Behind</div>
                    <div className="text-lg font-bold text-indigo-700">{recoveryStatus.completeness.estimatedTimeBehind}</div>
                  </div>
                  {recoveryStatus.ledgerRange && (
                    <div>
                      <div className="text-xs text-indigo-500">Current XRPL Ledger</div>
                      <div className="text-lg font-bold text-indigo-700">#{recoveryStatus.ledgerRange.current.toLocaleString()}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {recoveryUnavailable && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500">
                Ingestion service not reachable — recovery status and actions unavailable.
              </div>
            )}

            {/* Gaps Section */}
            {gapData && (
              <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                <div className={`px-4 py-3 border-b flex items-center justify-between ${
                  gapData.gapCount > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
                }`}>
                  <div>
                    <h3 className={`text-sm font-semibold ${gapData.gapCount > 0 ? 'text-red-800' : 'text-green-800'}`}>
                      {gapData.gapCount > 0
                        ? `Ledger Gaps Detected: ${gapData.gapCount} gap${gapData.gapCount !== 1 ? 's' : ''} (${gapData.totalMissing.toLocaleString()} missing)`
                        : 'No Gaps Detected'}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Range: #{gapData.range.first.toLocaleString()} - #{gapData.range.last.toLocaleString()} ({gapData.range.actual.toLocaleString()} of {gapData.range.expected.toLocaleString()} present)
                    </p>
                  </div>
                  {gapData.gapCount > 0 && !recoveryUnavailable && (
                    <button
                      onClick={() => handleAction('recover-gaps')}
                      disabled={actionLoading !== null}
                      className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      {actionLoading === 'recover-gaps' ? 'Starting...' : 'Recover All Gaps'}
                    </button>
                  )}
                </div>

                {gapData.gaps.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">End</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Missing Ledgers</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Est. Recovery</th>
                          {!recoveryUnavailable && (
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {gapData.gaps.map((gap) => {
                          // ~2 req/sec = ~0.5s per ledger
                          const estMinutes = Math.ceil(gap.size * 0.5 / 60);
                          return (
                            <tr key={gap.start} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-mono">{gap.start.toLocaleString()}</td>
                              <td className="px-4 py-3 text-sm font-mono">{gap.end.toLocaleString()}</td>
                              <td className="px-4 py-3 text-sm text-right font-medium text-red-700">{gap.size.toLocaleString()}</td>
                              <td className="px-4 py-3 text-sm text-right text-gray-500">
                                ~{estMinutes < 1 ? '<1' : estMinutes} min
                              </td>
                              {!recoveryUnavailable && (
                                <td className="px-4 py-3 text-right">
                                  <button
                                    onClick={() => handleAction('recover-range', {
                                      startIndex: gap.start,
                                      endIndex: gap.end,
                                    })}
                                    disabled={actionLoading !== null}
                                    className="rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200 disabled:opacity-50"
                                  >
                                    Recover
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Failed Ledgers Section */}
            {failureData && failureData.totalFailed > 0 && (
              <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 bg-red-50 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-red-800">
                    Failed Ledgers ({failureData.totalFailed})
                  </h3>
                  {!recoveryUnavailable && (
                    <button
                      onClick={() => handleAction('retry-failures', { maxRetries: 5, limit: 100 })}
                      disabled={actionLoading !== null}
                      className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      {actionLoading === 'retry-failures' ? 'Starting...' : 'Retry All Failures'}
                    </button>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ledger Index</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Retries</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Update</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {failureData.failures.map((f) => (
                        <tr key={f.ledgerIndex} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-mono">{f.ledgerIndex.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-red-600 max-w-xs truncate" title={f.errorMessage || undefined}>
                            {f.errorMessage || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={f.retryCount >= 3 ? 'text-red-600 font-medium' : ''}>
                              {f.retryCount}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">{formatDate(f.createdAt)}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{formatDate(f.updatedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Recent Ledgers */}
            {recentLedgers.length > 0 && (
              <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900">Recent Ledgers (Last 50)</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ledger</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">NFT TXs</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Retries</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Closed</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Processed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {recentLedgers.map((l) => (
                        <tr key={l.ledger_index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-mono">{l.ledger_index.toLocaleString()}</td>
                          <td className="px-4 py-3"><StatusBadge status={l.processing_status} /></td>
                          <td className="px-4 py-3 text-sm">{l.nft_transaction_count || 0}</td>
                          <td className="px-4 py-3 text-sm">
                            {l.retry_count > 0 ? (
                              <span className="text-orange-600">{l.retry_count}</span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-red-500 max-w-[200px] truncate" title={l.error_message || undefined}>
                            {l.error_message || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">{formatDate(l.close_time)}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{formatDate(l.processing_completed_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
